<script lang="ts" setup>
    import { computed } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useLogStore } from '~~/stores/log'
    import { useAppStore } from '~~/stores/app'
    import type { ThemeMode } from '~~/stores/app'
    import { useLoadingStore } from '~~/stores/loading'
    import { useEvents } from '~~/app/composables/useEvents'
    import { usePageTitle } from '~~/app/composables/usePageTitle'

    const logStore = useLogStore()
    const { entries } = storeToRefs(logStore)

    const { user, clear: clearSession } = useUserSession()

    const appStore = useAppStore()
    const loadingStore = useLoadingStore()
    const events = useEvents()

    const app = useNuxtApp()
    const vueVersion = app.vueApp.version
    const nuxtVersion = app.versions?.nuxt
    const towerVersion = useRuntimeConfig().public.appVersion

    const recentErrors = computed(
        () => entries.value
            .slice(0, 5)
            .some((entry) => entry.type === 'error'),
    )

    const router = useRouter()

    useHead({
        title: () => router.currentRoute.value.meta.title,
        titleTemplate: '%s - Boundless Tower',
    })

    const pageTitle = usePageTitle()

    const themeOptions: { mode: ThemeMode, label: string, icon: string }[] = [
        {
            mode: 'dark',
            label: 'Dark',
            icon: 'dark_mode',
        },
        {
            mode: 'light',
            label: 'Light',
            icon: 'light_mode',
        },
        {
            mode: 'auto',
            label: 'System',
            icon: 'brightness_auto',
        },
    ]

    const themeIcon = computed(() => {
        const current = themeOptions.find((option) => option.mode === appStore.themeMode)
        return current?.icon ?? 'brightness_auto'
    })

    async function logout() {
        await clearSession()
        await navigateTo('/login')
    }
</script>

<template>
    <q-toolbar>
        <q-btn
            flat
            dense
            round
            @click="appStore.toggleMenu"
            aria-label="Menu"
            icon="menu"
            v-if="!!user"
        />
        <q-avatar
            size="xl"
            color="info"
            :icon="router.currentRoute.value.meta.icon"
            style="margin-inline-start: 40px; margin-inline-end: 10px"
        />
        <q-toolbar-title data-testid="toolbar-title">{{ pageTitle }}</q-toolbar-title>
        <q-btn
            v-if="!!user && !events.connected"
            flat
            dense
            @click="events.reconnectNow()"
            aria-label="Reconnect SSE"
        >
            <q-icon
                name="sync_disabled"
                color="white"
                class="q-mr-xs"
                :class="{ 'toolbar-icon-spin': loadingStore.active }"
                data-testid="toolbar-disconnected-icon"
            />
            <span
                class="text-caption text-white"
                style="font-variant-numeric: tabular-nums"
                data-testid="toolbar-countdown"
            >
                {{ String(Math.floor(events.countdownSeconds / 60)).padStart(2, '0') }}:{{ String(events.countdownSeconds % 60).padStart(2, '0') }}
            </span>
        </q-btn>
        <q-icon
            v-if="!!user && events.connected"
            name="sync"
            color="white"
            class="q-mx-xs toolbar-icon"
            :class="{ 'toolbar-icon-spin': loadingStore.active }"
            data-testid="toolbar-connection-icon"
        />
        <q-btn
            v-if="!!user"
            flat
            dense
            round
            @click="appStore.toggleLog"
            aria-label="Log"
            icon="view_stream"
            :color="recentErrors ? 'red-8' : undefined"
        />
        <q-btn
            flat
            dense
            round
            aria-label="Mode"
            data-testid="theme-button"
            :icon="themeIcon"
        >
            <q-menu hover>
                <q-list>
                    <q-item
                        v-for="option in themeOptions"
                        :key="option.mode"
                        clickable
                        v-close-popup
                        :active="appStore.themeMode === option.mode"
                        :data-testid="`theme-option-${option.mode}`"
                        @click="appStore.setThemeMode(option.mode)"
                    >
                        <q-item-section avatar>
                            <q-icon :name="option.icon" />
                        </q-item-section>
                        <q-item-section>{{ option.label }}</q-item-section>
                    </q-item>
                </q-list>
            </q-menu>
        </q-btn>
        <q-btn-dropdown
            v-if="!!user"
            flat
            dense
            no-icon-animation
            :label="user?.name"
        >
            <q-list>
                <q-item
                    clickable
                    v-close-popup
                    @click="logout"
                >
                    <q-item-section avatar>
                        <q-icon name="logout" />
                    </q-item-section>
                    <q-item-section>Logout</q-item-section>
                </q-item>
                <q-separator />
                <q-item dense>
                    <q-item-section class="text-caption text-grey">Tower v{{ towerVersion }}</q-item-section>
                </q-item>
                <q-item dense>
                    <q-item-section class="text-caption text-grey">Vue v{{ vueVersion }}</q-item-section>
                </q-item>
                <q-item dense>
                    <q-item-section class="text-caption text-grey">Quasar v{{ $q.version }}</q-item-section>
                </q-item>
                <q-item
                    v-if="nuxtVersion"
                    dense
                >
                    <q-item-section class="text-caption text-grey">Nuxt v{{ nuxtVersion }}</q-item-section>
                </q-item>
            </q-list>
        </q-btn-dropdown>
    </q-toolbar>
</template>

<style lang="scss" scoped>
    @use 'sass:math';
    @use 'quasar/src/css/variables.sass' as quasar;

    .q-toolbar__title {
        padding-inline: 0;
    }

    // Match a q-btn's icon: Quasar renders it at $button-line-height (em),
    // resolved against the button's $button-font-size. Reproduce that as an
    // absolute size so a standalone icon tracks the same Quasar variables.
    .toolbar-icon {
        font-size: quasar.$button-font-size * math.div(quasar.$button-line-height, 1em);
    }

    .toolbar-icon-spin {
        animation: toolbar-icon-rotate 1s linear infinite;
    }

    @keyframes toolbar-icon-rotate {
        to {
            transform: rotate(360deg);
        }
    }
</style>
